// src/pages/QuickStart.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import {
  setComboProfile,
  setComboResult as saveComboResultToStore,
} from '../features/combo/comboSlice';
import { analyzeFaceCombo, ComboEntry } from '../services/api';
import { getVariantIdFromMap, parseSizeFromApi, variantsToMap } from '../utils/variantIdForSize';
import {
  Briefcase, Users, Gift, Sun, Camera, Check, ChevronUp,
  ShoppingCart, Truck, RotateCcw, Shield, CreditCard,
  X, Star, Play, Trash2, Lock, ChevronDown, ChevronLeft, ChevronRight, Share2,
} from 'lucide-react';
import { useToast } from '../components/ui/use-toast';
import { Helmet } from 'react-helmet-async';
import {
  trackViewStep1,
  trackSubmitStep1,
  trackViewStep2,
  trackSubmitStep2,
  trackViewStep3,
  trackStep3Select,
  trackSubmitStep3,
  trackViewAnalysing,
  trackCompleteLook,
  trackBeginCheckout,
  TrackItem,
} from '../utils/analytics';

// ─── Types & Constants ────────────────────────────────────────────────────────

type Phase = 'step1' | 'step2' | 'analyzing' | 'revealing' | 'combo-select' | 'generating' | 'result';

const OCCASIONS = [
  { id: 'office', label: 'Office', desc: 'Sharp and professional', Icon: Briefcase },
  { id: 'client_meeting', label: 'Client meeting', desc: 'Authoritative look', Icon: Users },
  { id: 'wedding', label: 'Wedding', desc: 'Celebratory style', Icon: Gift },
  { id: 'casual', label: 'Casual', desc: 'Relaxed smart', Icon: Sun },
];

const BUILDS = [
  { id: 'fit', label: 'Fit' },
  { id: 'average', label: 'Average' },
  { id: 'more_mass', label: 'Heavier' },
];

const comboVariantMapping: { [key: string]: { [size: string]: number } } = {
  brown_combo: {
    '6': 46336084377769, '7': 46336084410537, '8': 46336084443305,
    '9': 46336084476073, '10': 46336084508841, '11': 46806012887209,
  },
  black_combo: {
    '6': 46336068419753, '7': 46336068419753, '8': 46336068452521,
    '9': 46336068485289, '10': 46336068518057, '11': 46793993388201,
  },
};

const comboImages: { [key: string]: string } = {
  brown_combo: 'https://themanatomy.com/cdn/shop/files/29_240ad5e1-bd63-48eb-884b-e8ec2dfd33fb.png?v=1757599535&width=600',
  black_combo: 'https://themanatomy.com/cdn/shop/files/28_03bcf8da-5951-4930-a549-40b190195ab0.png?v=1757599976&width=600',
};

const SHOE_SIZES = ['6', '7', '8', '9', '10', '11'];
const STANDARD_SHIRT_SIZES = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
const STANDARD_PANT_SIZES = ['28', '30', '32', '34', '36', '38'];

const SKIN_SWATCHES: Record<string, string> = {
  fair: '#FAE0C8', light: '#F5C8A0', medium: '#C8915F', tan: '#A0724A',
  dark: '#5C3D1E', deep: '#3B2010', wheatish: '#D4A574', dusky: '#8B6347',
};
const HAIR_SWATCHES: Record<string, string> = {
  black: '#1A1A1A', brown: '#5C3D1E', dark: '#2C1810',
  blonde: '#D4A017', gray: '#808080', red: '#8B2500',
};
const EYE_SWATCHES: Record<string, string> = {
  brown: '#6B4226', dark: '#2C1810', black: '#1A1A1A', hazel: '#8B7355',
  green: '#3D6B3D', blue: '#4682B4', gray: '#6B6B6B',
};
const UNDERTONE_SWATCHES: Record<string, string> = {
  warm: '#D4A017', cool: '#E8A0A0', neutral: '#C8A878',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getSwatch(map: Record<string, string>, value: string): string {
  if (!value) return '#888888';
  const lower = value.toLowerCase();
  for (const key of Object.keys(map)) {
    if (lower.includes(key)) return map[key];
  }
  return '#888888';
}

function deriveUndertone(skinTone: string): { label: string; swatch: string } {
  const lower = skinTone.toLowerCase();
  if (lower.includes('warm') || lower.includes('golden') || lower.includes('brown') || lower.includes('olive') || lower.includes('tan') || lower.includes('medium') || lower.includes('wheatish') || lower.includes('dusky')) {
    return { label: 'Warm golden', swatch: UNDERTONE_SWATCHES.warm };
  }
  if (lower.includes('cool') || lower.includes('pink') || lower.includes('rose') || lower.includes('fair') || lower.includes('light')) {
    return { label: 'Cool rose', swatch: UNDERTONE_SWATCHES.cool };
  }
  return { label: 'Neutral beige', swatch: UNDERTONE_SWATCHES.neutral };
}

function getSizesFromVariantIds(
  variantIds: Record<string, string> | undefined,
  fallback: string[]
): string[] {
  if (!variantIds) return fallback;
  const keys = Object.keys(variantIds);
  if (keys.length === 0) return fallback;
  const parsed = keys.map(k => parseSizeFromApi(k) || k);
  const seen = new Set<string>();
  return parsed.filter(s => { if (seen.has(s)) return false; seen.add(s); return true; });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getProductImages(item: any): string[] {
  if (Array.isArray(item?.image_urls) && item.image_urls.length > 0) {
    // Guard against malformed payload where the array contains a single JSON-encoded string
    // e.g. image_urls: ["[\"https://...\", \"https://...\"]"]
    if (item.image_urls.length === 1 && typeof item.image_urls[0] === 'string' && item.image_urls[0].trimStart().startsWith('[')) {
      try {
        const parsed = JSON.parse(item.image_urls[0]);
        if (Array.isArray(parsed)) return parsed.filter((u: unknown) => typeof u === 'string' && u.startsWith('http'));
      } catch {
        // malformed JSON — fall back to regex extraction
        const extracted = (item.image_urls[0] as string).match(/https?:\/\/[^\s"']+/g);
        if (extracted && extracted.length > 0) return extracted;
      }
    }
    return item.image_urls;
  }
  if (item?.primary_image_url) return [item.primary_image_url];
  if (item?.image_url) return [item.image_url];
  return [];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getPrimaryImage(item: any): string {
  return getProductImages(item)[0] || '';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildModalMedia(item: any, fallback: ModalMediaItem[]): ModalMediaItem[] {
  const media: ModalMediaItem[] = [];
  if (item?.video_url) media.push({ type: 'video', url: item.video_url });
  for (const url of getProductImages(item)) media.push({ type: 'image', url });
  return media.length > 0 ? media : fallback;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getProductTags(item: any, fallback: string): string {
  if (Array.isArray(item?.product_tags) && item.product_tags.length > 0) {
    return item.product_tags.join(' · ');
  }
  return fallback;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getProductRating(item: any, fallback: number): number {
  return typeof item?.rating === 'number' ? item.rating : fallback;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getProductReviewCount(item: any, fallback: number): number {
  return typeof item?.review_count === 'number' ? item.review_count : fallback;
}

/** Parses the `fabric_details` JSON string (e.g. '{ "Breathable build": "Stays light..." }') into spec entries. */
function parseFabricDetails(json: string | undefined): { title: string; desc: string }[] {
  if (!json) return [];
  try {
    const obj = JSON.parse(json);
    if (obj && typeof obj === 'object') {
      return Object.entries(obj).map(([title, desc]) => ({ title, desc: String(desc).trim() }));
    }
  } catch {
    // malformed fabric_details — fall back to feature-based specs
  }
  return [];
}

function getLooksToday(): number {
  const base = 2100;
  const seed = new Date().getDate() * 7 + new Date().getMonth() * 31;
  return base + (seed % 400) + 41;
}

// ─── Header (dark variant for result/analyzing) ───────────────────────────────

interface QuickHeaderProps {
  step: number;
  total: number;
  onBack?: () => void;
  showBack?: boolean;
  dark?: boolean;
}

const QuickHeader: React.FC<QuickHeaderProps> = ({ step, total, onBack, showBack = false, dark = false }) => (
  <div className={`fixed top-0 left-0 right-0 z-50 ${dark ? 'bg-[#1A1A1A]' : 'bg-[#F5F0EB]'}`}>
    <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
      {showBack ? (
        <button onClick={onBack} className={`w-8 h-8 flex items-center justify-center transition-opacity hover:opacity-70 ${dark ? 'text-white' : 'text-[#1A1A1A]'}`}>
          <ChevronLeft className="w-5 h-5" />
        </button>
      ) : (
        <div className="w-8" />
      )}
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-[#C4875A]" />
        <span className={`text-sm font-semibold tracking-widest uppercase ${dark ? 'text-white' : 'text-[#1A1A1A]'}`}>MANATOMY · AI</span>
      </div>
      <span className={`text-xs font-medium ${dark ? 'text-[#6B6B6B]' : 'text-[#6B6B6B]'}`}>{step} / {total}</span>
    </div>
    {!dark && (
      <div className="h-0.5 bg-[#E5DDD5]">
        <div
          className="h-full bg-[#C4875A] transition-all duration-500"
          style={{ width: `${(step / total) * 100}%` }}
        />
      </div>
    )}
  </div>
);

// ─── Step 1 ───────────────────────────────────────────────────────────────────

interface Step1Props {
  occasion: string;
  setOccasion: (v: string) => void;
  build: string;
  setBuild: (v: string) => void;
  onContinue: () => void;
}

const Step1Phase: React.FC<Step1Props> = ({ occasion, setOccasion, build, setBuild, onContinue }) => {
  const canContinue = occasion !== '' && build !== '';
  const looksToday = getLooksToday();

  return (
    <div className="min-h-screen bg-[#F5F0EB] flex flex-col">
      <div className="h-[58px]" />
      <div className="flex-1 max-w-lg mx-auto w-full px-5 pt-5 pb-32">

        {/* Social proof badge */}
        <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-[#E5DDD5] mb-6">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-xs font-medium text-[#1A1A1A]">{looksToday.toLocaleString('en-IN')} looks created</span>
        </div>

        <div className="mb-7">
          <h1 className="text-3xl font-bold text-[#1A1A1A] leading-tight">
            What's the{' '}
            <span className="text-[#C4875A]">occasion</span>?
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-2 leading-relaxed">Your AI stylist will match colors and style to your specific need.</p>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-7">
          {OCCASIONS.map(({ id, label, desc, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setOccasion(id)}
              className={`p-4 rounded-2xl border-2 text-left transition-all ${
                occasion === id
                  ? 'border-[#1A1A1A] bg-white shadow-sm'
                  : 'border-[#E5DDD5] bg-white hover:border-[#C4875A]/50'
              }`}
            >
              <div className="mb-3">
                <Icon className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <p className="text-sm font-bold text-[#1A1A1A]">{label}</p>
              <p className="text-xs text-[#6B6B6B] mt-0.5">{desc}</p>
            </button>
          ))}
        </div>

        <div className="mb-8">
          <p className="text-[11px] font-bold text-[#6B6B6B] uppercase tracking-widest mb-3">Your Build</p>
          <div className="flex gap-2">
            {BUILDS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setBuild(id)}
                className={`flex-1 py-3 rounded-full border-2 text-sm font-semibold transition-all ${
                  build === id
                    ? 'border-[#1A1A1A] bg-[#1A1A1A] text-white'
                    : 'border-[#E5DDD5] bg-white text-[#1A1A1A] hover:border-[#C4875A]/50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-[#6B6B6B] text-center mb-5">✦ Free · No card · Takes 30 seconds</p>

        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          className={`w-full py-4 rounded-full text-base font-semibold flex items-center justify-center gap-2 transition-all ${
            canContinue
              ? 'bg-[#1A1A1A] text-white hover:bg-[#333]'
              : 'bg-[#E5DDD5] text-[#6B6B6B] cursor-not-allowed'
          }`}
        >
          Continue
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ─── Step 2 ───────────────────────────────────────────────────────────────────

interface Step2Props {
  name: string; setName: (v: string) => void;
  mobile: string; setMobile: (v: string) => void;
  feet: string; setFeet: (v: string) => void;
  inches: string; setInches: (v: string) => void;
  weight: string; setWeight: (v: string) => void;
  selfiePreview: string | null;
  errors: Record<string, string>;
  uploadRef: React.RefObject<HTMLInputElement>;
  cameraRef: React.RefObject<HTMLInputElement>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onBack: () => void;
  onSubmit: () => void;
}

const Step2Phase: React.FC<Step2Props> = ({
  name, setName, mobile, setMobile, feet, setFeet, inches, setInches,
  weight, setWeight, selfiePreview, errors, uploadRef, cameraRef,
  handleFileChange, setErrors, onBack, onSubmit,
}) => {
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);

  const openPicker = () => {
    setShowPhotoPicker(true);
  };

  return (
    <div className="min-h-screen bg-[#F5F0EB] flex flex-col">
      <div className="h-[58px]" />
      <div className="flex-1 max-w-lg mx-auto w-full px-5 pt-5 pb-32">

        <p className="text-lg font-semibold text-[#1A1A1A] leading-snug mb-6">
          AI reads your skin tone, eyes and hair to find colors built for your face.
        </p>

        {/* Selfie upload card */}
        <div
          onClick={openPicker}
          className={`relative w-full rounded-2xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer mb-3 overflow-hidden transition-all py-8 ${
            errors.selfie ? 'border-red-400 bg-red-50' : 'border-[#C4875A]/40 bg-white hover:border-[#C4875A]'
          }`}
        >
          {selfiePreview ? (
            <div className="relative">
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-green-500">
                <img src={selfiePreview} alt="Selfie" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-green-500 flex items-center justify-center border-2 border-white">
                <Check className="w-3.5 h-3.5 text-white" />
              </div>
            </div>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-[#F5F0EB] flex items-center justify-center mb-3">
                <Camera className="w-7 h-7 text-[#C4875A]" />
              </div>
              <p className="text-sm font-semibold text-[#1A1A1A]">Tap to take or upload a selfie</p>
              <p className="text-xs text-[#6B6B6B] mt-1">Face clearly visible · JPG or PNG</p>
            </>
          )}
        </div>

        {selfiePreview && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); openPicker(); }}
            className="w-full mb-3 py-2 rounded-xl border border-[#E5DDD5] bg-white text-sm font-medium text-[#1A1A1A] flex items-center justify-center gap-2 hover:border-[#C4875A]/50 transition-all"
          >
            <Camera className="w-4 h-4" /> Change photo
          </button>
        )}

        {/* Photo picker bottom sheet */}
        {showPhotoPicker && (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
            onClick={() => setShowPhotoPicker(false)}
          >
            <div
              className="w-full max-w-lg bg-white rounded-t-2xl px-5 pt-5 pb-8 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="w-10 h-1 bg-[#E5DDD5] rounded-full mx-auto mb-5" />
              <p className="text-base font-semibold text-[#1A1A1A] mb-4">Add your selfie</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowPhotoPicker(false); cameraRef.current?.click(); }}
                  className="flex-1 flex flex-col items-center gap-3 py-5 rounded-2xl border-2 border-[#E5DDD5] bg-[#F5F0EB] hover:border-[#C4875A] transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <Camera className="w-6 h-6 text-[#C4875A]" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-[#1A1A1A]">Take a photo</p>
                    <p className="text-[11px] text-[#6B6B6B] mt-0.5">Open camera</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => { setShowPhotoPicker(false); uploadRef.current?.click(); }}
                  className="flex-1 flex flex-col items-center gap-3 py-5 rounded-2xl border-2 border-[#E5DDD5] bg-[#F5F0EB] hover:border-[#C4875A] transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#C4875A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
                      <rect x="3" y="3" width="18" height="18" rx="3" ry="3" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-[#1A1A1A]">Upload from gallery</p>
                    <p className="text-[11px] text-[#6B6B6B] mt-0.5">Choose a photo</p>
                  </div>
                </button>
              </div>
              <button
                type="button"
                onClick={() => setShowPhotoPicker(false)}
                className="w-full mt-4 py-3 rounded-xl text-sm font-medium text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {errors.selfie && <p className="text-red-500 text-xs mb-3">{errors.selfie}</p>}

        {/* Privacy notice */}
        <div className="flex items-start gap-2 bg-green-50 rounded-xl p-3 mb-6">
          <Lock className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-green-800 leading-relaxed">Your photo is private — used only for AI analysis and deleted immediately after. Never stored or shared.</p>
        </div>

        {/* Body Metrics */}
        <p className="text-[11px] font-bold text-[#6B6B6B] uppercase tracking-widest mb-3">Body Metrics</p>

        <div className="space-y-3 mb-3">
          {/* Height */}
          <div className={`bg-white rounded-2xl border-2 px-4 py-3 transition-all ${errors.height ? 'border-red-400' : 'border-[#E5DDD5]'}`}>
            <p className="text-xs text-[#6B6B6B] mb-2">Height</p>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <select
                  value={feet}
                  onChange={e => { setFeet(e.target.value); setErrors(p => ({ ...p, height: '' })); }}
                  className="w-full text-sm font-semibold text-[#1A1A1A] bg-transparent outline-none appearance-none pr-5"
                >
                  <option value="">— ft</option>
                  {[4, 5, 6, 7].map(f => <option key={f} value={f}>{f}ft</option>)}
                </select>
                <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6B6B6B] pointer-events-none" />
              </div>
              <div className="w-px bg-[#E5DDD5]" />
              <div className="relative flex-1">
                <select
                  value={inches}
                  onChange={e => { setInches(e.target.value); setErrors(p => ({ ...p, height: '' })); }}
                  className="w-full text-sm font-semibold text-[#1A1A1A] bg-transparent outline-none appearance-none pr-5"
                >
                  <option value="">— in</option>
                  {Array.from({ length: 12 }, (_, i) => i).map(i => (
                    <option key={i} value={i}>{i}in</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6B6B6B] pointer-events-none" />
              </div>
            </div>
          </div>
          {errors.height && <p className="text-red-500 text-xs -mt-1">{errors.height}</p>}

          {/* Weight */}
          <div className={`bg-white rounded-2xl border-2 px-4 py-3 transition-all ${errors.weight ? 'border-red-400' : 'border-[#E5DDD5]'}`}>
            <p className="text-xs text-[#6B6B6B] mb-1">Weight</p>
            <input
              type="number"
              placeholder="70"
              value={weight}
              onChange={e => { setWeight(e.target.value); setErrors(p => ({ ...p, weight: '' })); }}
              className="w-full text-sm font-semibold text-[#1A1A1A] bg-transparent outline-none"
            />
          </div>
          {errors.weight && <p className="text-red-500 text-xs -mt-1">{errors.weight}</p>}

          {/* Name */}
          <div className={`bg-white rounded-2xl border-2 px-4 py-3 transition-all ${errors.name ? 'border-red-400' : 'border-[#E5DDD5]'}`}>
            <p className="text-xs text-[#6B6B6B] mb-1">Your Name</p>
            <input
              type="text"
              placeholder="Rahul Sharma"
              value={name}
              onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: '' })); }}
              className="w-full text-sm font-semibold text-[#1A1A1A] bg-transparent outline-none"
            />
          </div>
          {errors.name && <p className="text-red-500 text-xs -mt-1">{errors.name}</p>}

          {/* Mobile */}
          <div className={`bg-white rounded-2xl border-2 px-4 py-3 transition-all ${errors.mobile ? 'border-red-400' : 'border-[#E5DDD5]'}`}>
            <p className="text-xs text-[#6B6B6B] mb-1">Mobile Number</p>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="7065787678"
              value={mobile}
              onChange={e => { setMobile(e.target.value.replace(/\D/g, '').slice(0, 10)); setErrors(p => ({ ...p, mobile: '' })); }}
              className="w-full text-sm font-semibold text-[#1A1A1A] bg-transparent outline-none"
            />
          </div>
          {errors.mobile && <p className="text-red-500 text-xs -mt-1">{errors.mobile}</p>}
        </div>

        {/* Optional full body photo */}
        {/* <div className="bg-white rounded-2xl border border-[#E5DDD5] mb-6">
          <button
            type="button"
            onClick={() => setBodyPhotoOpen(p => !p)}
            className="w-full px-4 py-4 flex items-center justify-between"
          >
            <div>
              <p className="text-sm font-semibold text-[#1A1A1A] text-left">Add full body photo (optional)</p>
              <p className="text-xs text-[#6B6B6B] text-left mt-0.5">Makes avatar 40% more accurate</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-[#6B6B6B] transition-transform ${bodyPhotoOpen ? 'rotate-180' : ''}`} />
          </button>
          {bodyPhotoOpen && (
            <div className="px-4 pb-4">
              <p className="text-xs text-[#6B6B6B]">Full body photo upload coming soon.</p>
            </div>
          )}
        </div> */}

        <button
          type="button"
          onClick={() => {
            trackSubmitStep2({
              heightFt: feet,
              heightIn: inches,
              weightKg: weight,
              photoUploaded: selfiePreview !== null,
            });
            onSubmit();
          }}
          className="w-full py-4 rounded-full bg-[#1A1A1A] text-white text-base font-semibold flex items-center justify-center gap-2 hover:bg-[#333] transition-all"
        >
          Analyse my style
          <ChevronDown className="w-4 h-4" />
        </button>

        <input ref={uploadRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
        <input ref={cameraRef} type="file" accept="image/*" capture="user" onChange={handleFileChange} className="hidden" />
      </div>
    </div>
  );
};

// ─── Analyzing Phase ──────────────────────────────────────────────────────────

const AnalyzingPhase: React.FC<{ selfiePreview: string | null; title?: string; subtitle?: string }> = ({
  selfiePreview,
  title = 'Reading your features...',
  subtitle = 'Scanning skin, eyes and hair',
}) => (
  <div className="fixed inset-0 bg-[#1A1A1A] flex flex-col items-center justify-center z-40 px-6">
    <div className="flex flex-col items-center gap-8">
      <div className="relative w-40 h-40">
        <div className="absolute inset-0 rounded-full border-2 border-dashed border-[#C4875A]/30" style={{ animation: 'spin 10s linear infinite' }} />
        <div className="absolute inset-4 rounded-full border-2 border-[#C4875A] border-t-transparent animate-spin" />
        <div className="absolute inset-6 rounded-full overflow-hidden bg-[#2A2A2A]">
          {selfiePreview ? (
            <img src={selfiePreview} alt="Selfie" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Camera className="w-8 h-8 text-[#6B6B6B]" />
            </div>
          )}
        </div>
      </div>
      <div className="text-center">
        <p className="text-xl font-bold text-white mb-1">{title}</p>
        <p className="text-sm text-[#6B6B6B]">{subtitle}</p>
      </div>
    </div>
  </div>
);

// ─── Revealing Phase ──────────────────────────────────────────────────────────

interface RevealingPhaseProps {
  skinTone: string;
  eyeColor: string;
  hairColor: string;
  selfiePreview: string | null;
  onComplete: () => void;
}

const RevealingPhase: React.FC<RevealingPhaseProps> = ({ skinTone, eyeColor, hairColor, selfiePreview, onComplete }) => {
  const [revealed, setRevealed] = useState(0);
  const undertone = deriveUndertone(skinTone);

  const features = [
    { label: 'Skin Tone', value: skinTone || 'Detected', swatch: getSwatch(SKIN_SWATCHES, skinTone) },
    { label: 'Undertone', value: undertone.label, swatch: undertone.swatch },
    { label: 'Eye Color', value: eyeColor || 'Detected', swatch: getSwatch(EYE_SWATCHES, eyeColor) },
    { label: 'Hair', value: hairColor ? `Deep ${hairColor.toLowerCase()}` : 'Detected', swatch: getSwatch(HAIR_SWATCHES, hairColor) },
  ];

  useEffect(() => {
    const timers = [
      setTimeout(() => setRevealed(1), 600),
      setTimeout(() => setRevealed(2), 1500),
      setTimeout(() => setRevealed(3), 2400),
      setTimeout(() => setRevealed(4), 3300),
      setTimeout(() => onComplete(), 4800),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 bg-[#1A1A1A] flex flex-col items-center justify-center z-40 px-6">
      {/* Selfie circle */}
      <div className="relative w-28 h-28 mb-6">
        <div className="absolute inset-0 rounded-full border-2 border-dashed border-[#C4875A]/40" />
        <div className="absolute inset-3 rounded-full overflow-hidden bg-[#2A2A2A]">
          {selfiePreview ? (
            <img src={selfiePreview} alt="You" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Camera className="w-6 h-6 text-[#6B6B6B]" />
            </div>
          )}
        </div>
      </div>

      <h2 className="text-xl font-bold text-white mb-6 text-center">Reading your features...</h2>

      <div className="w-full max-w-sm space-y-3 mb-6">
        {features.map((f, i) => (
          <div
            key={f.label}
            className="flex items-center gap-3 transition-all duration-500"
            style={{
              opacity: revealed > i ? 1 : 0.2,
              transform: revealed > i ? 'translateX(0)' : 'translateX(-8px)',
            }}
          >
            <div className="w-8 h-8 rounded-full flex-shrink-0 border border-white/10" style={{ background: f.swatch }} />
            <div className="flex-1">
              <p className="text-[10px] text-[#6B6B6B] uppercase tracking-widest font-semibold">{f.label}</p>
              <p className="text-sm font-semibold text-white capitalize">{f.value}</p>
            </div>
            {revealed > i && (
              <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
            )}
          </div>
        ))}
      </div>

      <div className="w-full max-w-sm">
        <div className="h-0.5 bg-white/10 rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-[#C4875A] rounded-full transition-all duration-700"
            style={{ width: `${(revealed / features.length) * 100}%` }}
          />
        </div>
        <p className="text-xs text-center text-[#6B6B6B]">Almost ready...</p>
      </div>
    </div>
  );
};

// ─── Combo Select Phase ───────────────────────────────────────────────────────

const COMBO_MATCH_PCT = [97, 95, 93];

function deriveColorSwatch(colorName: string | undefined, fallback: string): string {
  if (!colorName) return fallback;
  const lower = colorName.toLowerCase();
  if (lower.includes('olive') || (lower.includes('green') && !lower.includes('sea'))) return '#4A7C59';
  if (lower.includes('sage')) return '#7B9E87';
  if (lower.includes('navy')) return '#1E3A5F';
  if (lower.includes('steel') || lower.includes('slate') || (lower.includes('blue') && !lower.includes('powder'))) return '#4A6FA5';
  if (lower.includes('powder') || lower.includes('sky')) return '#5BA4CF';
  if (lower.includes('beige') || lower.includes('khaki') || lower.includes('sand')) return '#C4A882';
  if (lower.includes('cream') || lower.includes('ivory') || lower.includes('off-white')) return '#EDE5D6';
  if (lower.includes('white')) return '#F5F0EB';
  if (lower.includes('charcoal') || lower.includes('black')) return '#2C2C2C';
  if (lower.includes('grey') || lower.includes('gray')) return '#6B7280';
  if (lower.includes('brown') || lower.includes('tan') || lower.includes('chestnut')) return '#8B6347';
  if (lower.includes('maroon') || lower.includes('burgundy') || lower.includes('wine')) return '#8B2020';
  if (lower.includes('mustard') || lower.includes('golden') || lower.includes('amber')) return '#C4A017';
  if (lower.includes('teal') || lower.includes('cyan') || lower.includes('turquoise')) return '#4A9B9B';
  if (lower.includes('purple') || lower.includes('violet') || lower.includes('lavender')) return '#6B4A8B';
  if (lower.includes('pink') || lower.includes('rose')) return '#E8A0A0';
  return fallback;
}

function getComboColors(combo: ComboEntry): { shirt: string; pant: string; shoes: string } {
  return {
    shirt: combo.shirt?.hex_code || deriveColorSwatch(combo.shirt?.color, '#888888'),
    pant: combo.pant?.hex_code || deriveColorSwatch(combo.pant?.color, '#2C2C2C'),
    shoes: combo.shoes?.hex_code || (combo.shoes?.combo_id?.includes('brown') ? '#8B6347' : '#1A1A1A'),
  };
}

function getComboTitle(combo: ComboEntry): string {
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  const shirt = combo.shirt?.color ? cap(combo.shirt.color) : 'Shirt';
  const pant = combo.pant?.color ? combo.pant.color.toLowerCase() : 'pant';
  const shoes = combo.shoes?.combo_id?.includes('brown') ? 'brown shoes' : 'black shoes';
  return `${shirt} shirt, ${pant} pant, ${shoes}`;
}

function getComboDescription(combo: ComboEntry): string {
  const color = (combo.shirt?.color || '').toLowerCase();
  if (color.includes('olive') || (color.includes('green') && !color.includes('sea')))
    return 'Olive green sits close to your warm golden undertone, making your skin look naturally sun-kissed.';
  if (color.includes('blue') || color.includes('steel') || color.includes('navy') || color.includes('slate'))
    return 'Steel blue cools your warm undertone for an authoritative, boardroom-ready look.';
  if (color.includes('beige') || color.includes('cream') || color.includes('khaki') || color.includes('sand'))
    return 'Beige sits directly in your warm family — soft and approachable for casual Fridays.';
  if (color.includes('white') || color.includes('ivory'))
    return 'Crisp white creates sharp contrast against your skin tone for a clean, polished look.';
  if (color.includes('grey') || color.includes('gray'))
    return 'Subtle grey balances your features while keeping the look refined and versatile.';
  if (color.includes('maroon') || color.includes('burgundy') || color.includes('wine'))
    return 'Deep tones like this add gravitas and frame your face for a commanding presence.';
  if (color.includes('mustard') || color.includes('golden') || color.includes('amber'))
    return 'Warm mustard amplifies your golden undertone and adds a confident, distinctive edge.';
  if (color.includes('teal') || color.includes('cyan'))
    return 'Teal bridges warm and cool tones, bringing out depth and richness in your complexion.';
  return `${combo.shirt?.color || 'This shade'} is calibrated to your undertone for a naturally confident, polished look.`;
}

interface ComboSelectPhaseProps {
  combos: ComboEntry[];
  faceAnalysis: { hair_color: string; skin_tone: string; eye_color: string };
  selectedIndex: number;
  onSelect: (idx: number) => void;
  onConfirm: () => void;
  onBack: () => void;
}

const ComboSelectPhase: React.FC<ComboSelectPhaseProps> = ({
  combos, faceAnalysis, selectedIndex, onSelect, onConfirm, onBack,
}) => {
  const undertone = deriveUndertone(faceAnalysis.skin_tone);
  console.log("le beta1:", faceAnalysis);

  const headline = undertone.label.toLowerCase().includes('warm')
    ? 'These colours naturally make your skin look brighter and more confident.'
    : undertone.label.toLowerCase().includes('cool')
    ? 'These colours sharpen your features and enhance your natural clarity.'
    : 'These colours balance your undertone for a polished, confident look.';

  const subDesc = undertone.label.toLowerCase().includes('warm')
    ? 'Earthy tones and warm blues enhance your depth and presence. Cool greys will wash you out.'
    : undertone.label.toLowerCase().includes('cool')
    ? 'Cool blues and neutral tones complement your clarity. Heavy warm tones may overpower.'
    : 'A balanced mix of warm and neutral tones works beautifully for your undertone.';

  return (
    <div className="min-h-screen bg-[#F5F0EB] pb-36">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#F5F0EB]">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={onBack} className="w-8 h-8 flex items-center justify-center text-[#1A1A1A] hover:opacity-70 transition-opacity">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#C4875A]" />
            <span className="text-sm font-semibold tracking-widest text-[#1A1A1A] uppercase">MANATOMY · AI</span>
          </div>
          <span className="text-xs font-medium text-[#6B6B6B]">3 / 5</span>
        </div>
        <div className="h-0.5 bg-[#E5DDD5]">
          <div className="h-full bg-[#C4875A] transition-all duration-500" style={{ width: '60%' }} />
        </div>
      </div>

      <div className="h-[58px]" />
      <div className="max-w-lg mx-auto px-4 pt-5">

        {/* Colour Palette Card */}
        <div className="bg-white rounded-2xl border border-[#E5DDD5] p-5 mb-5 shadow-sm">
          <p className="text-[10px] font-bold text-[#6B6B6B] uppercase tracking-widest mb-4 text-center">
            Your Personalised Colour Palette
          </p>

          {/* We Analysed checklist */}
          {/* <div className="mb-5">
            <p className="text-xs text-[#6B6B6B] mb-3">We analysed</p>
            <div className="grid grid-cols-2 gap-y-2.5 gap-x-4">
              {['Skin tone', 'Undertone', 'Facial contrast', 'Hair colour', 'Eye colour', 'Face shape'].map(item => (
                <div key={item} className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded border-2 border-[#C4875A] flex items-center justify-center flex-shrink-0">
                    <Check className="w-2.5 h-2.5 text-[#C4875A]" strokeWidth={3} />
                  </div>
                  <span className="text-xs text-[#1A1A1A]">{item}</span>
                </div>
              ))}
            </div>
          </div> */}

          {/* Detected features as pills */}
          <div className="flex gap-2 flex-wrap mb-5 justify-center">
            {faceAnalysis.skin_tone && (
              <div className="flex flex-col items-center gap-1.5 px-3 py-1.5">
                <div className="bg-[#F5F0EB] rounded-full w-10 h-10 border-2 border-black/30 flex-shrink-0" style={{ background: getSwatch(SKIN_SWATCHES, faceAnalysis.skin_tone) }} />
                <span className="text-xs text-[#1A1A1A] capitalize">Skin Tone</span>
              </div>
            )}
            <div className="flex flex-col items-center gap-1.5 px-3 py-1.5">
              <div className="bg-[#F5F0EB] rounded-full w-10 h-10 border-2 border-black/30 flex-shrink-0" style={{ background: undertone.swatch }} />
              <span className="text-xs text-[#1A1A1A]">Undertone</span>
            </div>
            {faceAnalysis.eye_color && (
              <div className="flex flex-col items-center gap-1.5 px-3 py-1.5">
                <div className="bg-[#F5F0EB] rounded-full w-10 h-10 border-2 border-black/30 flex-shrink-0" style={{ background: getSwatch(EYE_SWATCHES, faceAnalysis.eye_color) }} />
                <span className="text-xs text-[#1A1A1A] capitalize">Eyes Color</span>
              </div>
            )}
            {faceAnalysis.hair_color && (
              <div className="flex flex-col items-center gap-1.5 px-3 py-1.5">
                <div className="bg-[#F5F0EB] rounded-full w-10 h-10 border-2 border-black/30 flex-shrink-0" style={{ background: faceAnalysis.hair_color }} />
                <span className="text-xs text-[#1A1A1A] capitalize">Hair Color</span>
              </div>
            )}
          </div>

          {/* Headline + sub */}
          <h2 className="text-[17px] font-bold text-[#1A1A1A] text-center leading-snug mb-2">
            {headline}
          </h2>
          <p className="text-xs text-[#6B6B6B] text-center leading-relaxed">{subDesc}</p>
        </div>

        {/* Section title */}
        <div className="mb-4">
          <h3 className="text-xl font-bold text-[#1A1A1A] mb-1">
            {combos.length} picks built for this palette
          </h3>
          <p className="text-xs text-[#6B6B6B] leading-relaxed">
            After comparing your facial features with 48 colour combinations, we found the colours that make you look more confident and professional.
          </p>
        </div>

        {/* Combo Cards */}
        <div className="space-y-3 mb-5">
          {combos.map((combo, idx) => {
            const colors = getComboColors(combo);
            const title = getComboTitle(combo);
            const description = getComboDescription(combo);
            const matchPct = COMBO_MATCH_PCT[idx] ?? (93 - idx * 2);
            const isBest = idx === 0;
            const isSelected = idx === selectedIndex;

            return (
              <button
                key={idx}
                type="button"
                onClick={() => onSelect(idx)}
                className={`w-full text-left rounded-2xl border-2 p-4 transition-all duration-200 ${
                  isSelected
                    ? 'border-[#C4875A] bg-white shadow-md'
                    : 'border-[#E5DDD5] bg-white hover:border-[#C4875A]/40'
                }`}
              >
                {/* Swatches + title + selector */}
                <div className="flex items-start gap-3 mb-2.5">
                  {/* Color swatches */}
                  <div className="flex gap-1.5 flex-shrink-0 mt-0.5">
                    <div className="w-8 h-8 rounded-lg border border-black/10 shadow-sm" style={{ background: colors.shirt }} />
                    <div className="w-8 h-8 rounded-lg border border-black/10 shadow-sm" style={{ background: colors.pant }} />
                    <div className="w-8 h-8 rounded-lg border border-black/10 shadow-sm" style={{ background: colors.shoes }} />
                  </div>

                  {/* Title + badge */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#1A1A1A] leading-snug mb-1.5">{title}</p>
                    {isBest ? (
                      <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full">
                        🏆 Best match · {matchPct}%
                      </span>
                    ) : (
                      <span className="inline-flex items-center bg-[#F5F0EB] border border-[#E5DDD5] text-[#6B6B6B] text-xs font-medium px-2.5 py-1 rounded-full">
                        {matchPct}% match
                      </span>
                    )}
                  </div>

                  {/* Radio circle */}
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                    isSelected ? 'border-[#C4875A] bg-[#C4875A]' : 'border-[#E5DDD5] bg-white'
                  }`}>
                    {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </div>
                </div>

                <p className="text-xs text-[#6B6B6B] leading-relaxed mb-1.5">{description}</p>
                <p className="text-[10px] text-[#C4875A] font-medium">Out of 48 colour combinations tested for you.</p>
              </button>
            );
          })}
        </div>

        {/* Quality + social proof card */}
        <div className="bg-white rounded-2xl border border-[#E5DDD5] p-4 mb-3 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-4 h-4 rounded border-2 border-[#C4875A] flex items-center justify-center flex-shrink-0">
              <Check className="w-2.5 h-2.5 text-[#C4875A]" strokeWidth={3} />
            </div>
            <p className="text-sm font-medium text-[#1A1A1A]">Built to last, checked before it ships</p>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <div className="flex">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
              ))}
            </div>
            <span className="text-sm font-bold text-[#1A1A1A]">4.8/5</span>
            <span className="text-xs text-[#6B6B6B]">· 2,743 customers</span>
          </div>
          <p className="text-xs text-[#6B6B6B]">220 GSM premium micro piqué fabric</p>
        </div>

        {/* Exchange notice */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3 mb-2">
          <div className="w-5 h-5 rounded border-2 border-green-500 flex items-center justify-center flex-shrink-0">
            <Check className="w-3 h-3 text-green-500" strokeWidth={3} />
          </div>
          <p className="text-sm text-green-700 font-medium">Not convinced once it arrives? Free 15-day exchange.</p>
        </div>

      </div>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#F5F0EB]/95 backdrop-blur-sm px-4 py-4 border-t border-[#E5DDD5]">
        <div className="max-w-lg mx-auto">
          <button
            type="button"
            onClick={onConfirm}
            className="w-full py-4 rounded-full bg-[#1A1A1A] text-white text-base font-semibold flex items-center justify-center gap-2 hover:bg-[#333] transition-all shadow-lg"
          >
            <Camera className="w-4 h-4" />
            Preview this look on me
          </button>
          <p className="text-[11px] text-center text-[#6B6B6B] mt-2">
            Takes about 10 seconds. Creates a realistic preview using your uploaded photo.
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Result Phase ─────────────────────────────────────────────────────────────

interface ResultPhaseProps {
  combos: ComboEntry[];
  avatarUrls: (string | null)[];
  avatarErrors: boolean[];
  occasion: string;
  onBack: () => void;
  mobile: string | null;
}

type ModalMediaItem = { type: 'video'; url: string } | { type: 'image'; url: string };

// TODO: replace with per-product media from DB
const PLACEHOLDER_MODAL_MEDIA: ModalMediaItem[] = [
  { type: 'video', url: 'https://cdn.shopify.com/videos/c/o/v/b0e25c2095a04cc596ec3ffae600310a.mp4' },
  { type: 'image', url: 'https://cdn.shopify.com/s/files/1/0669/5236/4201/files/1.webp' },
  { type: 'image', url: 'https://cdn.shopify.com/s/files/1/0669/5236/4201/files/3.webp' },
];

const PRODUCT_FEATURES: Record<string, string> = {
  shirt: '160 GSM · Wrinkle resistant · UV protected',
  top: '160 GSM · Wrinkle resistant · UV protected',
  pant: '4-way stretch · Flexible waistband',
  pants: '4-way stretch · Flexible waistband',
  shoes: 'Genuine leather · Cushioned insole',
};

const FEATURE_SPECS: Record<string, { title: string; desc: string }> = {
  '160 GSM': { title: 'Breathable build', desc: '160 GSM fabric stays light through long days' },
  'Wrinkle resistant': { title: 'Always fresh', desc: 'Stays sharp and crease-free, morning to night' },
  'UV protected': { title: 'UV shield', desc: 'Built-in protection for outdoor wear' },
  '4-way stretch': { title: 'Full flexibility', desc: '4-way stretch adapts to every move' },
  'Flexible waistband': { title: 'All-day comfort', desc: 'Waistband flexes with you, never digs in' },
  'Genuine leather': { title: 'Premium materials', desc: 'Genuine leather built to last' },
  'Cushioned insole': { title: 'Cushioned support', desc: 'Soft insole keeps every step comfortable' },
};

const ResultPhase: React.FC<ResultPhaseProps> = ({
  combos, avatarUrls, avatarErrors, mobile, occasion, onBack,
}) => {
  const { toast } = useToast();
  const [activeComboIndex, setActiveComboIndex] = useState(0);
  const [shoeSize, setShoeSize] = useState('9');
  const [shirtSize, setShirtSize] = useState('');
  const [pantSize, setPantSize] = useState('');
  const [imageZoomOpen, setImageZoomOpen] = useState(false);
  const [removedShirt, setRemovedShirt] = useState(false);
  const [removedPant, setRemovedPant] = useState(false);
  const [removedShoes, setRemovedShoes] = useState(false);
  const [productModal, setProductModal] = useState<'shirt' | 'pant' | 'shoes' | null>(null);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const activeCombo = combos[activeComboIndex] ?? combos[0];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = activeCombo as any;
  const currentAvatarUrl = avatarUrls[activeComboIndex];
  const isCurrentAvatarLoading = !avatarUrls[activeComboIndex] && !avatarErrors[activeComboIndex];

  // Reset sizes and removed state when switching combos
  useEffect(() => {
    setRemovedShirt(false);
    setRemovedPant(false);
    setRemovedShoes(false);
    const shirt = activeCombo?.shirt;
    const pant = activeCombo?.pant;
    const shoes = activeCombo?.shoes;
    const sSize = shirt?.recommended_size || shirt?.size;
    if (sSize) setShirtSize(parseSizeFromApi(sSize) || sSize);
    const pSize = pant?.recommended_size || pant?.size;
    if (pSize) setPantSize(parseSizeFromApi(pSize) || pSize);
    if (shoes?.recommended_size) setShoeSize(shoes.recommended_size);
    else setShoeSize('9');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeComboIndex]);

  const shoesComboId = activeCombo?.shoes?.combo_id || 'brown_combo';
  const shoesName = activeCombo?.shoes?.name || (shoesComboId.includes('brown') ? 'Legacy Brown Shoes & Belt' : 'Legacy Black Shoes & Belt');
  const shoesPrice = parseFloat(String(activeCombo?.shoes?.price ?? '')) || 3499;
  const shoesOriginalPrice = parseFloat(activeCombo?.shoes?.regular_price || '') || 5999;
  const shoesImage = getPrimaryImage(activeCombo?.shoes) || comboImages[shoesComboId] || '';

  const shirtPrice = parseFloat(r.shirt?.price || '0');
  const pantPrice = parseFloat(r.pant?.price || '0');
  const shirtRegular = parseFloat(r.shirt?.regular_price || '0') || shirtPrice;
  const pantRegular = parseFloat(r.pant?.regular_price || '0') || pantPrice;
  const totalPrice = (!removedShirt ? shirtPrice : 0) + (!removedPant ? pantPrice : 0) + (!removedShoes ? shoesPrice : 0);
  const totalRegular = (!removedShirt ? shirtRegular : 0) + (!removedPant ? pantRegular : 0) + (!removedShoes ? shoesOriginalPrice : 0);
  const savings = Math.max(0, totalRegular - totalPrice);
  const savingsPct = totalRegular > 0 ? Math.round((savings / totalRegular) * 100) : 0;
  const extraDiscountPct = 15;
  const extraDiscountAmount = Math.round(totalPrice * extraDiscountPct / 100);
  const finalPrice = totalPrice - extraDiscountAmount;

  const confidence = 90 + ((r.shirt?.color?.charCodeAt(0) || 3) % 8);
  const occasionLabel = OCCASIONS.find(o => o.id === occasion)?.label || 'Office';

  const shirtVariantIds = r.shirt?.variants ? variantsToMap(r.shirt.variants) : r.shirt?.variant_ids;
  const pantVariantIds = r.pant?.variants ? variantsToMap(r.pant.variants) : r.pant?.variant_ids;
  const shirtSizes = getSizesFromVariantIds(shirtVariantIds, STANDARD_SHIRT_SIZES);
  const pantSizes = getSizesFromVariantIds(pantVariantIds, STANDARD_PANT_SIZES);

  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    const items: TrackItem[] = [
      r.shirt ? { item_id: r.shirt.name, item_name: r.shirt.name, price: shirtPrice, quantity: 1 } : null,
      r.pant ? { item_id: r.pant.name, item_name: r.pant.name, price: pantPrice, quantity: 1 } : null,
      { item_id: shoesName, item_name: shoesName, price: shoesPrice, quantity: 1 },
    ].filter(Boolean) as TrackItem[];
    trackCompleteLook({ items, value: totalPrice });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShare = async () => {
    if (!currentAvatarUrl || isSharing) return;
    const avatarUrl = currentAvatarUrl;
    setIsSharing(true);
    try {
      let shared = false;

      // Try sharing the actual image file (may fail due to CORS on S3)
      try {
        const response = await fetch(avatarUrl);
        const blob = await response.blob();
        const file = new File([blob], 'my-manatomy-look.jpg', { type: blob.type || 'image/jpeg' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'My AI-Styled Look by Manatomy',
            text: 'Check out the outfit my AI stylist picked for me at Manatomy!',
          });
          shared = true;
        }
      } catch {
        // CORS or fetch blocked — fall through to URL sharing
      }

      if (!shared) {
        if (navigator.share) {
          await navigator.share({
            title: 'My AI-Styled Look by Manatomy',
            text: 'Check out the outfit my AI stylist picked for me at Manatomy!',
            url: avatarUrl,
          });
        } else {
          await navigator.clipboard.writeText(avatarUrl);
          toast({ title: 'Link copied!', description: 'Paste it anywhere to share your look.' });
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        toast({ title: 'Could not share', description: 'Try copying the link instead.', variant: 'destructive' });
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleBuyLook = () => {
    const comboId = activeCombo?.shoes?.combo_id || 'brown_combo';
    const shoeVariant = comboVariantMapping[comboId]?.[shoeSize];
    const params: string[] = [];
    if (!removedShirt) {
      const shirtVariant = getVariantIdFromMap(shirtVariantIds, shirtSize) || r.shirt?.recommended_variant_id || r.shirt?.variant_id;
      if (shirtVariant) params.push(`${shirtVariant}:1`);
    }
    if (!removedPant) {
      const pantVariant = getVariantIdFromMap(pantVariantIds, pantSize) || r.pant?.recommended_variant_id || r.pant?.variant_id;
      if (pantVariant) params.push(`${pantVariant}:1`);
    }
    if (!removedShoes && shoeVariant) params.push(`${shoeVariant}:1`);
    if (params.length === 0) {
      toast({ title: 'Checkout unavailable', description: 'Could not build cart. Try again.', variant: 'destructive' });
      return;
    }
    const checkoutItems: TrackItem[] = [
      !removedShirt && r.shirt ? { item_id: r.shirt.name, item_name: r.shirt.name, price: shirtPrice, quantity: 1 } : null,
      !removedPant && r.pant ? { item_id: r.pant.name, item_name: r.pant.name, price: pantPrice, quantity: 1 } : null,
      !removedShoes ? { item_id: shoesName, item_name: shoesName, price: shoesPrice, quantity: 1 } : null,
    ].filter(Boolean) as TrackItem[];
    trackBeginCheckout({ value: finalPrice, coupon: 'manatomyai', items: checkoutItems });
    window.location.href = `https://themanatomy.com/cart/${params.join(',')}?attributes[AI_Avatar]=${currentAvatarUrl}&discount=manatomyai&utm_campaign=avatar_check&utm_medium=quick_look&utm_source=ai_beta&checkout[mobile]=${mobile}`;
  };

  let modalImages: ModalMediaItem[] = [];
  let modalName = '';
  let modalPrice = 0;
  let modalRegularPrice = 0;
  let modalColor: string | undefined;
  let modalFeatures = '';
  let modalRating = '4.8';
  let modalReviews = '847 reviews';
  let modalSpecs: { title: string; desc: string }[] = [];
  if (productModal === 'shirt' && r.shirt) {
    modalImages = buildModalMedia(r.shirt, PLACEHOLDER_MODAL_MEDIA);
    modalName = r.shirt.name;
    modalPrice = shirtPrice;
    modalRegularPrice = shirtRegular;
    modalColor = r.shirt.color;
    modalFeatures = getProductTags(r.shirt, PRODUCT_FEATURES.shirt);
    modalRating = getProductRating(r.shirt, 4.8).toFixed(1);
    modalReviews = `${getProductReviewCount(r.shirt, 847)} reviews`;
    modalSpecs = parseFabricDetails(r.shirt.fabric_details);
  } else if (productModal === 'pant' && r.pant) {
    modalImages = buildModalMedia(r.pant, PLACEHOLDER_MODAL_MEDIA);
    modalName = r.pant.name;
    modalPrice = pantPrice;
    modalRegularPrice = pantRegular;
    modalColor = r.pant.color;
    modalFeatures = getProductTags(r.pant, PRODUCT_FEATURES.pant);
    modalRating = getProductRating(r.pant, 4.9).toFixed(1);
    modalReviews = `${getProductReviewCount(r.pant, 623)} reviews`;
    modalSpecs = parseFabricDetails(r.pant.fabric_details);
  } else if (productModal === 'shoes') {
    modalImages = buildModalMedia(activeCombo?.shoes, PLACEHOLDER_MODAL_MEDIA);
    modalName = shoesName;
    modalPrice = shoesPrice;
    modalRegularPrice = shoesOriginalPrice;
    modalColor = activeCombo?.shoes?.color;
    modalFeatures = getProductTags(activeCombo?.shoes, PRODUCT_FEATURES.shoes);
    modalRating = getProductRating(activeCombo?.shoes, 4.7).toFixed(1);
    modalReviews = `${getProductReviewCount(activeCombo?.shoes, 1243)} reviews`;
    modalSpecs = parseFabricDetails(activeCombo?.shoes?.fabric_details);
  }

  const modalDetailLabel = productModal === 'shoes' ? 'Material detail' : 'Fabric details';
  const modalFeatureList = modalFeatures.split(' · ').filter(Boolean);
  if (modalSpecs.length === 0) {
    modalSpecs = modalFeatureList.map(f => FEATURE_SPECS[f] || { title: f, desc: '' });
  }

  return (
    <div className="min-h-screen bg-[#F5F0EB] pb-32">

      {/* Dark sticky header */}
      <div className="sticky top-0 z-30 bg-[#1A1A1A]">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={onBack} className="w-8 h-8 flex items-center justify-center text-white hover:opacity-70 transition-opacity" title="Back">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#C4875A]" />
            <span className="text-sm font-semibold tracking-widest text-white uppercase">MANATOMY · AI</span>
          </div>
          <span className="text-xs text-[#6B6B6B]">4 / 5</span>
        </div>
      </div>

      {/* Avatar section — swipeable carousel */}
      <div
        className="relative flex justify-center select-none"
        onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={e => {
          if (touchStartX.current === null) return;
          const delta = e.changedTouches[0].clientX - touchStartX.current;
          touchStartX.current = null;
          if (Math.abs(delta) < 50) return;
          if (delta < 0) setActiveComboIndex(i => Math.min(i + 1, combos.length - 1));
          else setActiveComboIndex(i => Math.max(i - 1, 0));
        }}
      >
        <div className="relative w-full max-w-lg bg-[#E8DDD0] aspect-[9/16] overflow-hidden">
          {currentAvatarUrl ? (
            <>
              <img
                src={currentAvatarUrl}
                alt="Your AI-Generated Look"
                className="w-full object-cover cursor-pointer h-full"
                onClick={() => setImageZoomOpen(true)}
              />
              {/* Top badges */}
              <div className="absolute top-4 left-4">
                <div className="flex items-center gap-1.5 bg-[#1A1A1A] rounded-full px-3 py-1.5">
                  <span className="text-white text-xs">✦</span>
                  <span className="text-white text-xs font-semibold">AI Pick</span>
                </div>
              </div>
              <div className="absolute top-4 right-4">
                <div className="bg-white rounded-full px-3 py-1.5">
                  <span className="text-[#1A1A1A] text-xs font-semibold">{occasionLabel}</span>
                </div>
              </div>
              {/* Confidence badge */}
              <div className="absolute bottom-4 left-4">
                <div className="bg-white/95 rounded-2xl px-3 py-2.5 flex items-center gap-3">
                  <div className="relative w-10 h-10">
                    <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="15" fill="none" stroke="#E5DDD5" strokeWidth="3" />
                      <circle cx="18" cy="18" r="15" fill="none" stroke="#22C55E" strokeWidth="3"
                        strokeDasharray={`${(confidence / 100) * 94} 94`} />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-sm font-bold text-[#1A1A1A]">{confidence}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#1A1A1A] leading-none">AI match</p>
                    <p className="text-[10px] text-[#6B6B6B] leading-none mt-0.5">confidence</p>
                  </div>
                </div>
              </div>
              {/* <div className="absolute bottom-4 right-4">
                <div className="flex items-center gap-1.5 bg-[#1A1A1A]/80 rounded-full px-3 py-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  <span className="text-white text-xs font-medium">12 styling this now</span>
                </div>
              </div> */}
            </>
          ) : isCurrentAvatarLoading ? (
            <>
              {getPrimaryImage(activeCombo?.shirt) && (
                <img src={getPrimaryImage(activeCombo.shirt)} alt="" className="w-full h-full object-cover opacity-25" />
              )}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <div className="w-12 h-12 border-t-transparent rounded-full animate-spin" style={{ borderWidth: 3, borderStyle: 'solid', borderColor: '#C4875A', borderTopColor: 'transparent' }} />
                <div className="text-center">
                  <p className="text-sm font-semibold text-[#1A1A1A]">Crafting your look…</p>
                  <p className="text-xs text-[#6B6B6B] mt-1">AI avatar generating</p>
                </div>
              </div>
            </>
          ) : (
            <>
              {getPrimaryImage(activeCombo?.shirt) ? (
                <img src={getPrimaryImage(activeCombo.shirt)} alt="Your Look" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <p className="text-sm text-[#6B6B6B]">Image unavailable</p>
                </div>
              )}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                <div className="bg-white/90 rounded-full px-4 py-2">
                  <p className="text-xs text-[#6B6B6B]">Shirt preview · Avatar unavailable</p>
                </div>
              </div>
            </>
          )}

          {/* Prev / Next arrows — desktop + tablet */}
          {combos.length > 1 && activeComboIndex > 0 && (
            <button
              type="button"
              onClick={() => setActiveComboIndex(i => i - 1)}
              className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 items-center justify-center transition-colors"
              aria-label="Previous look"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
          )}
          {combos.length > 1 && activeComboIndex < combos.length - 1 && (
            <button
              type="button"
              onClick={() => setActiveComboIndex(i => i + 1)}
              className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 items-center justify-center transition-colors"
              aria-label="Next look"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          )}
        </div>
      </div>

      {/* Combo picker dock — below avatar */}
      {combos.length > 1 && (
        <div className="max-w-lg mx-auto px-4 pt-3 pb-1">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold text-[#6B6B6B] uppercase tracking-widest">
              {combos.length} AI-matched looks
            </p>
            <p className="text-[10px] text-[#6B6B6B]">Swipe or tap to switch</p>
          </div>
          <div className="flex gap-2.5">
            {combos.map((combo, idx) => {
              const isActive = idx === activeComboIndex;
              const url = avatarUrls[idx];
              const isLoading = !url && !avatarErrors[idx];
              const shirtImg = getPrimaryImage(combo.shirt);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setActiveComboIndex(idx)}
                  className={`flex-1 relative rounded-xl overflow-hidden transition-all duration-200 ${
                    isActive
                      ? 'ring-2 ring-[#1A1A1A] ring-offset-2 shadow-lg'
                      : 'ring-1 ring-[#E5DDD5] hover:ring-[#C4875A]/50'
                  }`}
                >
                  {/* Thumbnail */}
                  <div className="h-[90px] bg-[#E8DDD0] relative">
                    {shirtImg && <img src={shirtImg} alt="" className="w-full h-full object-cover" />}
                    {url && <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                    {isLoading && (
                      <div className="absolute inset-0 bg-black/45 flex flex-col items-center justify-center gap-1">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span className="text-[8px] font-medium text-white/90">Generating</span>
                      </div>
                    )}
                    {/* Rank badge */}
                    <div className={`absolute top-1 left-1 rounded-full px-1.5 py-0.5 ${idx === 0 ? 'bg-[#C4875A]' : 'bg-black/55'}`}>
                      <span className="text-[8px] font-bold text-white leading-none">{idx === 0 ? '★ Best' : `#${idx + 1}`}</span>
                    </div>
                    {/* Active checkmark */}
                    {isActive && (
                      <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                  {/* Label */}
                  <div className={`py-1.5 transition-colors ${isActive ? 'bg-[#1A1A1A]' : 'bg-white'}`}>
                    <p className={`text-[10px] font-semibold text-center leading-none ${isActive ? 'text-white' : 'text-[#6B6B6B]'}`}>
                      Look {idx + 1}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto px-4">
        {/* Share button */}
        {currentAvatarUrl && (
          <div className="flex justify-center py-4 border-b border-[#E5DDD5]">
            <button
              type="button"
              onClick={handleShare}
              disabled={isSharing}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full border border-[#E5DDD5] bg-white text-sm font-medium text-[#1A1A1A] hover:border-[#C4875A] transition-all disabled:opacity-50"
            >
              <Share2 className="w-4 h-4 text-[#C4875A]" />
              {isSharing ? 'Sharing…' : 'Share my look'}
            </button>
          </div>
        )}

        {/* Explanation quote */}
        {r.shirt && (
          <div className="py-5 border-b border-[#E5DDD5]">
            <p className="text-sm text-[#6B6B6B] italic leading-relaxed text-center">
              Tones chosen to complement your undertone — polished and authoritative.
            </p>
          </div>
        )}

        {/* Worn by section */}
        {/* <div className="py-5 border-b border-[#E5DDD5]">
          <div className="flex items-center justify-between mb-1">
            <p className="text-base font-bold text-[#1A1A1A]">Worn by men like you</p>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
              <span className="text-xs font-medium text-green-600">5 verified</span>
            </div>
          </div>
          <p className="text-xs text-[#6B6B6B] mb-4">Real customers · same occasion · similar skin tone</p>
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-4 px-4">
            {WORN_BY_PHOTOS.map((src, i) => (
              <div key={i} className="relative flex-shrink-0 w-28 h-36 rounded-2xl overflow-hidden bg-[#E5DDD5]">
                <img src={src} alt="Customer look" className="w-full h-full object-cover" />
                <div className="absolute top-2 left-2 bg-[#1A1A1A]/80 rounded px-1.5 py-0.5">
                  <span className="text-[9px] font-bold text-white tracking-wide">SAME LOOK</span>
                </div>
              </div>
            ))}
          </div>
        </div> */}

        {/* Products heading */}
        <div className="pt-5 pb-3 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-[#1A1A1A]">Your AI-curated look</h2>
            <p className="text-xs text-[#6B6B6B] mt-0.5">All items matched to your skin tone.</p>
          </div>
          {savingsPct > 0 && (
            <div className="flex items-center gap-1 bg-orange-50 border border-orange-200 rounded-full px-3 py-1.5 flex-shrink-0 ml-3">
              <span className="text-orange-500 text-xs">🔥</span>
              <span className="text-xs font-bold text-orange-600">Save {savingsPct}%</span>
            </div>
          )}
        </div>

        {/* Product Cards */}
        <div className="space-y-3 mb-4">
          {/* Shirt */}
          {r.shirt && !removedShirt && (
            <div className="bg-white rounded-2xl p-4 border border-[#E5DDD5]">
              <div className="flex gap-3 mb-3 cursor-pointer" onClick={() => { setProductModal('shirt'); setModalImageIndex(0); }}>
                <img src={getPrimaryImage(r.shirt)} alt={r.shirt.name}
                  className="w-20 h-20 object-cover rounded-xl flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#1A1A1A] leading-tight">{r.shirt.name}</p>
                  <p className="text-xs text-[#6B6B6B] mt-1">{getProductTags(r.shirt, PRODUCT_FEATURES.shirt)}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                      <span className="text-xs font-semibold text-[#1A1A1A]">{getProductRating(r.shirt, 4.8).toFixed(1)}</span>
                      <span className="text-xs text-[#6B6B6B]">· {getProductReviewCount(r.shirt, 847)} reviews</span>
                    </div>
                    {r.shirt.video_url && (
                      <button type="button" className="flex items-center gap-1 bg-[#F5F0EB] rounded-full px-2 py-0.5">
                        <Play className="w-2.5 h-2.5 text-[#1A1A1A]" />
                        <span className="text-[10px] font-medium text-[#1A1A1A]">Product Details</span>
                      </button>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2 mt-1.5">
                    <p className="text-base font-bold text-[#1A1A1A]">{shirtPrice.toLocaleString('en-IN')}</p>
                    {shirtRegular > shirtPrice && (
                      <p className="text-xs text-[#6B6B6B] line-through">{shirtRegular.toLocaleString('en-IN')}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-[#6B6B6B] uppercase tracking-wide flex-shrink-0">Size</span>
                <div className="flex gap-1.5 flex-1 flex-wrap">
                  {shirtSizes.map(size => (
                    <button key={size} type="button" onClick={() => setShirtSize(size)}
                      className={`min-w-[2.25rem] h-8 px-2 text-xs font-semibold rounded-lg border-2 transition-all ${
                        shirtSize === size
                          ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                          : 'bg-white text-[#1A1A1A] border-[#E5DDD5]'
                      }`}>{size}</button>
                  ))}
                </div>
                <button type="button" onClick={() => setRemovedShirt(true)} className="flex items-center gap-1 text-[#6B6B6B] hover:text-red-400 transition-colors flex-shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                  <span className="text-xs">Remove</span>
                </button>
              </div>
            </div>
          )}

          {/* Pants */}
          {r.pant && !removedPant && (
            <div className="bg-white rounded-2xl p-4 border border-[#E5DDD5]">
              <div className="flex gap-3 mb-3 cursor-pointer" onClick={() => { setProductModal('pant'); setModalImageIndex(0); }}>
                <img src={getPrimaryImage(r.pant)} alt={r.pant.name}
                  className="w-20 h-20 object-cover rounded-xl flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#1A1A1A] leading-tight">{r.pant.name}</p>
                  <p className="text-xs text-[#6B6B6B] mt-1">{getProductTags(r.pant, PRODUCT_FEATURES.pant)}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                      <span className="text-xs font-semibold text-[#1A1A1A]">{getProductRating(r.pant, 4.9).toFixed(1)}</span>
                      <span className="text-xs text-[#6B6B6B]">· {getProductReviewCount(r.pant, 623)} reviews</span>
                    </div>
                      <button type="button" className="flex items-center gap-1 bg-[#F5F0EB] rounded-full px-2 py-0.5">
                        <Play className="w-2.5 h-2.5 text-[#1A1A1A]" />
                        <span className="text-[10px] font-medium text-[#1A1A1A]">Product Details</span>
                      </button>
                  </div>
                  <div className="flex items-baseline gap-2 mt-1.5">
                    <p className="text-base font-bold text-[#1A1A1A]">{pantPrice.toLocaleString('en-IN')}</p>
                    {pantRegular > pantPrice && (
                      <p className="text-xs text-[#6B6B6B] line-through">{pantRegular.toLocaleString('en-IN')}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-[#6B6B6B] uppercase tracking-wide flex-shrink-0">Size</span>
                <div className="flex gap-1.5 flex-1 flex-wrap">
                  {pantSizes.map(size => (
                    <button key={size} type="button" onClick={() => setPantSize(size)}
                      className={`min-w-[2.25rem] h-8 px-2 text-xs font-semibold rounded-lg border-2 transition-all ${
                        pantSize === size
                          ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                          : 'bg-white text-[#1A1A1A] border-[#E5DDD5]'
                      }`}>{size}</button>
                  ))}
                </div>
                <button type="button" onClick={() => setRemovedPant(true)} className="flex items-center gap-1 text-[#6B6B6B] hover:text-red-400 transition-colors flex-shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                  <span className="text-xs">Remove</span>
                </button>
              </div>
            </div>
          )}

          {/* Belt & Shoes */}
          {!removedShoes && <div className="bg-white rounded-2xl p-4 border border-[#E5DDD5]">
            <div className="flex gap-3 mb-3 cursor-pointer" onClick={() => { setProductModal('shoes'); setModalImageIndex(0); }}>
              <img src={shoesImage} alt={shoesName}
                className="w-20 h-20 object-cover rounded-xl flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#1A1A1A] leading-tight">{shoesName}</p>
                <p className="text-xs text-[#6B6B6B] mt-1">{getProductTags(activeCombo?.shoes, PRODUCT_FEATURES.shoes)}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                    <span className="text-xs font-semibold text-[#1A1A1A]">{getProductRating(activeCombo?.shoes, 4.7).toFixed(1)}</span>
                    <span className="text-xs text-[#6B6B6B]">· {getProductReviewCount(activeCombo?.shoes, 1243)} reviews</span>
                  </div>
                  {activeCombo?.shoes?.video_url && (
                    <button type="button" className="flex items-center gap-1 bg-[#F5F0EB] rounded-full px-2 py-0.5">
                      <Play className="w-2.5 h-2.5 text-[#1A1A1A]" />
                      <span className="text-[10px] font-medium text-[#1A1A1A]">Product Details</span>
                    </button>
                  )}
                </div>
                <div className="flex items-baseline gap-2 mt-1.5">
                  <p className="text-base font-bold text-[#1A1A1A]">{shoesPrice.toLocaleString('en-IN')}</p>
                  <p className="text-xs text-[#6B6B6B] line-through">{shoesOriginalPrice.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-[#6B6B6B] uppercase tracking-wide flex-shrink-0">Size</span>
              <div className="flex gap-1.5 flex-1 flex-wrap">
                {SHOE_SIZES.map(size => (
                  <button key={size} type="button" onClick={() => setShoeSize(size)}
                    className={`min-w-[2.25rem] h-8 px-2 text-xs font-semibold rounded-lg border-2 transition-all ${
                      shoeSize === size
                        ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                        : 'bg-white text-[#1A1A1A] border-[#E5DDD5]'
                    }`}>{size}</button>
                ))}
              </div>
              <button type="button" onClick={() => setRemovedShoes(true)} className="flex items-center gap-1 text-[#6B6B6B] hover:text-red-400 transition-colors flex-shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
                <span className="text-xs">Remove</span>
              </button>
            </div>
          </div>}
        </div>

        {/* Price summary */}
        <div className="bg-white rounded-2xl border border-[#E5DDD5] mb-6 overflow-hidden">
          <div className="p-4 space-y-2.5">
            {r.shirt && !removedShirt && (
              <div className="flex justify-between text-sm">
                <span className="text-[#6B6B6B]">{r.shirt.name}</span>
                <span className="font-medium text-[#1A1A1A]">Rs. {shirtPrice.toLocaleString('en-IN')}</span>
              </div>
            )}
            {r.pant && !removedPant && (
              <div className="flex justify-between text-sm">
                <span className="text-[#6B6B6B]">{r.pant.name}</span>
                <span className="font-medium text-[#1A1A1A]">Rs. {pantPrice.toLocaleString('en-IN')}</span>
              </div>
            )}
            {!removedShoes && (
              <div className="flex justify-between text-sm">
                <span className="text-[#6B6B6B]">{shoesName}</span>
                <span className="font-medium text-[#1A1A1A]">Rs. {shoesPrice.toLocaleString('en-IN')}</span>
              </div>
            )}
          </div>
          <div className="border-t border-[#E5DDD5] px-4 pt-3 pb-1 flex justify-between text-sm">
            <span className="text-[#6B6B6B]">Subtotal</span>
            <span className="font-medium text-[#1A1A1A]">Rs. {totalPrice.toLocaleString('en-IN')}</span>
          </div>
          <div className="px-4 pb-3 flex justify-between text-sm items-center">
            <div className="flex items-center gap-1.5">
              <span className="text-green-600 font-medium">Discount</span>
              <span className="bg-green-50 border border-green-200 text-green-700 text-[10px] font-bold px-1.5 py-0.5 rounded tracking-wide">MANATOMYAI</span>
              <span className="text-green-600 font-medium">({extraDiscountPct}%)</span>
            </div>
            <span className="font-medium text-green-600">− Rs. {extraDiscountAmount.toLocaleString('en-IN')}</span>
          </div>
          <div className="border-t border-[#E5DDD5] px-4 py-3 flex justify-between">
            <span className="font-bold text-[#1A1A1A]">You pay</span>
            <span className="font-bold text-[#C4875A] text-lg">Rs. {finalPrice.toLocaleString('en-IN')}</span>
          </div>
        </div>

        {/* Trust row */}
        <div className="grid grid-cols-4 gap-2 mb-6">
          {[
            { Icon: Truck, label: 'Free Delivery' },
            { Icon: RotateCcw, label: 'Easy Exchange' },
            { Icon: Shield, label: '15-day Return' },
            { Icon: CreditCard, label: 'COD Available' },
          ].map(({ Icon, label }) => (
            <div key={label} className="flex flex-col items-center gap-1.5 bg-white rounded-xl p-2.5 border border-[#E5DDD5]">
              <Icon className="w-4 h-4 text-[#C4875A]" />
              <p className="text-[10px] font-medium text-[#1A1A1A] text-center leading-tight">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Sticky buy CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#F5F0EB]/95 backdrop-blur-sm p-4">
        <div className="max-w-lg mx-auto">
          <button
            type="button"
            onClick={handleBuyLook}
            className="w-full py-4 rounded-full bg-[#1A1A1A] text-white text-base font-semibold flex items-center justify-center gap-2 hover:bg-[#333] transition-all shadow-lg"
          >
            <ShoppingCart className="w-4 h-4" />
            Buy my look — Rs. {finalPrice.toLocaleString('en-IN')} →
          </button>
          <p className="text-[11px] text-center text-[#6B6B6B] mt-2">🔒 Secure · No upfront payment · COD available</p>
        </div>
      </div>

      {/* Product Detail Modal */}
      {productModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          role="dialog" aria-modal="true"
          onClick={() => setProductModal(null)}
        >
          <div
            className="relative w-full max-w-[380px] max-h-[90vh] overflow-y-auto bg-[#FAF9F7] rounded-[20px] shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button type="button" onClick={() => setProductModal(null)}
              className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-white/90 flex items-center justify-center shadow-sm">
              <X className="w-3 h-3 text-[#1A1A18]" strokeWidth={2.5} />
            </button>

            {/* Detail pill */}
            <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 bg-white/90 rounded-full pl-2.5 pr-3 py-1.5 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C4875A]" />
              <span className="text-[10px] font-medium text-[#1A1A18] tracking-wide">{modalDetailLabel}</span>
            </div>

            {/* Media carousel — 9:16 */}
            <div className="relative w-full aspect-[1] bg-[#1A1A18] overflow-hidden">
              {modalImages[modalImageIndex]?.type === 'video' ? (
                <video
                  key={modalImages[modalImageIndex].url}
                  src={modalImages[modalImageIndex].url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover opacity-[0.93]"
                />
              ) : (
                <img src={modalImages[modalImageIndex]?.url || ''} alt={modalName}
                  className="w-full h-full object-cover opacity-[0.93]" />
              )}

              {/* Feature banner */}
              {modalFeatureList.length > 0 && (
                <div className="absolute bottom-0 left-0 right-0 px-3.5 pt-12 pb-3 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
                  <p className="italic text-white text-sm tracking-wide">
                    {modalFeatureList[modalImageIndex % modalFeatureList.length]}
                  </p>
                </div>
              )}

              {modalImages.length > 1 && (
                <>
                  <button type="button"
                    onClick={() => setModalImageIndex(i => (i - 1 + modalImages.length) % modalImages.length)}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/20 border border-white/20 hover:bg-white/20 transition-colors flex items-center justify-center">
                    <ChevronDown className="w-3 h-3 text-white rotate-90" strokeWidth={2.5} />
                  </button>
                  <button type="button"
                    onClick={() => setModalImageIndex(i => (i + 1) % modalImages.length)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/20 border border-white/20 hover:bg-white/20 transition-colors flex items-center justify-center">
                    <ChevronDown className="w-3 h-3 text-white -rotate-90" strokeWidth={2.5} />
                  </button>
                  <div className="absolute bottom-3 right-3.5 flex items-center gap-1 z-10">
                    {modalImages.map((_, i) => (
                      <button key={i} type="button" onClick={() => setModalImageIndex(i)}
                        className={`h-1 rounded-full transition-all ${i === modalImageIndex ? 'w-3.5 bg-white' : 'w-1 bg-white/35'}`} />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Content */}
            <div className="px-4 pt-3.5 pb-5">
              <div className="flex justify-between items-start gap-3 mb-0.5">
                <h2 className="text-[18px] font-semibold text-[#1A1A18] leading-[1.2] flex-1">
                  {modalName}
                </h2>
                <div className="text-right flex-shrink-0 ml-3">
                  <p className="text-[16px] font-medium text-[#1A1A18] whitespace-nowrap">Rs. {modalPrice.toLocaleString('en-IN')}</p>
                  {modalRegularPrice > modalPrice && (
                    <p className="text-[11px] text-[#bbbbbb] line-through mt-0.5 whitespace-nowrap">Rs. {modalRegularPrice.toLocaleString('en-IN')}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1.5 mb-3">
                <span className="text-[#C8963E] text-[11px] tracking-tighter">★★★★★</span>
                <span className="text-[11px] text-[#999999] font-light">{modalRating} · {modalReviews}</span>
              </div>

              {modalColor && (
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="text-[11px] font-medium text-[#1A1A18]">Color</span>
                  <span className="text-[11px] text-[#999999] capitalize">{modalColor}</span>
                </div>
              )}

              <hr className="border-t border-[#eceae7] mb-3" />

              {/* Feature chips */}
              {modalFeatureList.length > 0 && (
                <div className="flex flex-wrap gap-[5px] mb-3">
                  {modalFeatureList.map(f => (
                    <span key={f} className="text-[11px] text-[#555555] bg-[#efede9] rounded-full px-2.5 py-[3px]">{f}</span>
                  ))}
                </div>
              )}

              {/* Spec grid */}
              {modalSpecs.length > 0 && (
                <div className="grid grid-cols-2 gap-[7px] mb-4">
                  {modalSpecs.map(spec => (
                    <div key={spec.title} className="bg-[#f3f1ed] rounded-[10px] px-[11px] py-[9px]">
                      <p className="text-[11px] font-medium text-[#1A1A18] mb-0.5 leading-tight">{spec.title}</p>
                      {spec.desc && <p className="text-[10px] text-[#999999] leading-[1.4]">{spec.desc}</p>}
                    </div>
                  ))}
                </div>
              )}

              {/* Size selector */}
              {/* <div>
                <p className="text-[11px] font-medium text-[#1A1A18] uppercase tracking-wide mb-2">Select size</p>
                <div className="flex gap-1.5 flex-wrap">
                  {modalSizes.map(size => (
                    <button key={size} type="button" onClick={() => modalOnSizeChange(size)}
                      className={`min-w-[2.5rem] h-9 px-2.5 text-xs font-medium rounded-lg border transition-all ${
                        modalSelectedSize === size
                          ? 'bg-[#1A1A18] text-white border-[#1A1A18]'
                          : 'bg-white text-[#1A1A18] border-[#E5DDD5] hover:border-[#1A1A18]'
                      }`}>{size}</button>
                  ))}
                </div>
              </div> */}

              {/* {modalUrl && (
                <a href={modalUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-block mt-4 text-[11px] text-[#C4875A] underline underline-offset-2">
                  View on site
                </a>
              )} */}
            </div>
          </div>
        </div>
      )}

      {/* Zoom modal */}
      {imageZoomOpen && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" role="dialog" aria-modal="true">
          <div className="flex justify-end p-4">
            <button onClick={() => setImageZoomOpen(false)}
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center p-4 min-h-0">
            <img src={currentAvatarUrl || ''} alt="Your AI Look" className="max-w-full max-h-full object-contain" />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SESSION_KEY = 'qs_form_v1';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function readSavedSession(): any {
  try {
    const saved = sessionStorage.getItem(SESSION_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

// Phases that were mid-network-request can't be resumed after a reload, and
// phases past step2 need their AI results to render — fall back to the
// closest step that has what it needs.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveInitialPhase(d: any): Phase {
  if (!d?.phase) return 'step1';
  const hasFaceAnalysis = !!d.faceAnalysis;
  const hasCombos = Array.isArray(d.combosList) && d.combosList.length > 0;
  const hasResultCombo = !!d.resultCombo;
  switch (d.phase) {
    case 'analyzing':
      return 'step2';
    case 'generating':
      return hasFaceAnalysis && hasCombos ? 'combo-select' : 'step2';
    case 'combo-select':
    case 'revealing':
      return hasFaceAnalysis && hasCombos ? d.phase : 'step2';
    case 'result':
      if (hasResultCombo) return 'result';
      return hasFaceAnalysis && hasCombos ? 'combo-select' : 'step2';
    default:
      return d.phase as Phase;
  }
}

const QuickStart: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { toast } = useToast();

  // Seed state directly from session storage on the very first render, rather
  // than restoring in a post-mount effect — that left a window where a sibling
  // effect could persist the pre-restore defaults and clobber the saved phase.
  const [initialSession] = useState(() => readSavedSession());

  const [phase, setPhase] = useState<Phase>(() => resolveInitialPhase(initialSession));
  const [combosList, setCombosList] = useState<ComboEntry[]>(() => (Array.isArray(initialSession?.combosList) ? initialSession.combosList : []));
  const [faceAnalysis, setFaceAnalysis] = useState<{ hair_color: string; skin_tone: string; eye_color: string } | null>(() => initialSession?.faceAnalysis || null);
  const [avatarUrls, setAvatarUrls] = useState<(string | null)[]>(() => (Array.isArray(initialSession?.avatarUrls) ? initialSession.avatarUrls : [null, null, null]));
  const [avatarErrors, setAvatarErrors] = useState<boolean[]>(() => (Array.isArray(initialSession?.avatarErrors) ? initialSession.avatarErrors : [false, false, false]));
  const [selectedComboIndex, setSelectedComboIndex] = useState(() => (typeof initialSession?.selectedComboIndex === 'number' ? initialSession.selectedComboIndex : 0));
  const [resultCombo, setResultCombo] = useState<ComboEntry | null>(() => initialSession?.resultCombo || null);
  const [resultAvatarUrl, setResultAvatarUrl] = useState<string | null>(() => initialSession?.resultAvatarUrl || null);
  const [resultAvatarError, setResultAvatarError] = useState<boolean>(() => !!initialSession?.resultAvatarError);
  const [triggerBackgroundGen, setTriggerBackgroundGen] = useState(0);

  const [occasion, setOccasion] = useState(() => initialSession?.occasion || '');
  const [build, setBuild] = useState(() => initialSession?.build || '');
  const [name, setName] = useState(() => initialSession?.name || '');
  const [mobile, setMobile] = useState(() => initialSession?.mobile || '');
  const [feet, setFeet] = useState(() => initialSession?.feet || '');
  const [inches, setInches] = useState(() => (initialSession?.inches !== undefined && initialSession?.inches !== null ? String(initialSession.inches) : ''));
  const [weight, setWeight] = useState(() => initialSession?.weight || '');
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(() => (initialSession?.selfieBase64 ? `data:image/jpeg;base64,${initialSession.selfieBase64}` : null));
  const [selfieBase64, setSelfieBase64] = useState<string | null>(() => initialSession?.selfieBase64 || null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Persist form data and flow state to session storage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        occasion, build, name, mobile, feet, inches, weight, selfieBase64,
        phase, combosList, faceAnalysis, avatarUrls, avatarErrors, selectedComboIndex,
        resultCombo, resultAvatarUrl, resultAvatarError,
      }));
    } catch {
      // session storage unavailable or quota exceeded
    }
  }, [occasion, build, name, mobile, feet, inches, weight, selfieBase64, phase, combosList, faceAnalysis, avatarUrls, avatarErrors, selectedComboIndex, resultCombo, resultAvatarUrl, resultAvatarError]);

  const uploadRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    if (input.files?.[0]) {
      const file = input.files[0];
      setSelfiePreview(URL.createObjectURL(file));
      setSelfieFile(file);
      setErrors(prev => ({ ...prev, selfie: '' }));
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setSelfieBase64(result.split(',')[1] || result);
      };
      reader.readAsDataURL(file);
    }
    input.value = '';
  };

  useEffect(() => {
    if (phase === 'step1') trackViewStep1();
    else if (phase === 'step2') trackViewStep2();
    else if (phase === 'combo-select') trackViewStep3();
    else if (phase === 'analyzing') trackViewAnalysing();
  }, [phase]);

  const handleStep1Continue = () => {
    if (!occasion || !build) return;
    trackSubmitStep1(occasion, build);
    setPhase('step2');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateStep2 = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Name is required';
    if (!mobile.trim()) e.mobile = 'Mobile number is required';
    else if (!/^\d{10}$/.test(mobile.trim())) e.mobile = 'Enter a valid 10-digit mobile number';
    if (!feet || inches === '') e.height = 'Both feet and inches are required';
    const w = parseInt(weight);
    if (!weight || isNaN(w) || w < 40 || w > 200) e.weight = 'Weight must be between 40 and 200 kg';
    if (!selfieFile && !selfieBase64) e.selfie = 'A selfie is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleStep2Submit = async () => {
    if (!validateStep2()) {
      toast({ title: 'Complete your profile', description: 'Please fill out all the details.', variant: 'destructive' });
      return;
    }
    setErrors({});

    let fileToSend = selfieFile;
    if (!fileToSend && selfieBase64) {
      try {
        const bin = atob(selfieBase64);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        fileToSend = new File([new Blob([arr], { type: 'image/jpeg' })], 'selfie.jpg', { type: 'image/jpeg' });
      } catch {
        toast({ title: 'Selfie error', description: 'Please re-upload your selfie.', variant: 'destructive' });
        return;
      }
    }
    if (!fileToSend) {
      toast({ title: 'Selfie required', description: 'Please upload your selfie.', variant: 'destructive' });
      return;
    }

    const height = `${feet}'${inches}`;
    dispatch(setComboProfile({
      selfieBase64: selfieBase64 || '', selfieUrl: selfiePreview || '',
      name, mobile, height, weight, bodyType: build,
    }));

    setPhase('analyzing');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const analysisStart = Date.now();
    try {
      const raw = await analyzeFaceCombo({
        file: fileToSend, name, email: '', mobile, height, weight, bodyType: build, profession: occasion, age: '', occasion,
      });

      console.info('[QuickStart] Raw API response keys:', Object.keys(raw));

      if (raw.is_human_face === false) {
        setPhase('step2');
        toast({ title: 'No Face Detected', description: 'Please upload a valid selfie with your face visible.', variant: 'destructive' });
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const r = raw as any;

      // Normalize combos — support new array format and legacy single-combo format
      let normalizedCombos: ComboEntry[];
      if (Array.isArray(r.combos) && r.combos.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        normalizedCombos = r.combos.map((c: any) => ({
          rank: c.rank ?? 1,
          shirt: c.shirt || c.top || null,
          pant: c.pant || c.pants || c.trouser || c.trousers || c.bottom || null,
          shoes: c.shoes || c.footwear || null,
        }));
      } else {
        // Backward compat: single combo from top-level fields
        normalizedCombos = [{
          rank: 1,
          shirt: r.shirt || r.top || r.recommended_shirt || null,
          pant: r.pant || r.pants || r.trouser || r.trousers || r.bottom || null,
          shoes: r.shoes || r.footwear || r.combo_shoes || null,
        }];
      }

      console.info('[QuickStart] Normalized combos:', normalizedCombos.length, '| combo1 shirt:', normalizedCombos[0]?.shirt?.name);

      setFaceAnalysis({ hair_color: raw.hair_color || '', skin_tone: raw.skin_tone || '', eye_color: raw.eye_color || '' });
      setCombosList(normalizedCombos);
      setAvatarUrls(new Array(normalizedCombos.length).fill(null));
      setAvatarErrors(new Array(normalizedCombos.length).fill(false));

      // Save combo 1 to Redux store for legacy flows
      const c1 = normalizedCombos[0];
      dispatch(saveComboResultToStore({
        shirt: c1.shirt, pant: c1.pant, shoes: c1.shoes,
        hair_color: raw.hair_color, skin_tone: raw.skin_tone,
        eye_color: raw.eye_color, face_type: raw.face_type,
      }));


      const elapsed = Date.now() - analysisStart;
      if (elapsed < 2000) await new Promise(res => setTimeout(res, 2000 - elapsed));

      setPhase('revealing');
    } catch (error) {
      console.error('[ERROR] analyzeFaceCombo failed:', error);
      setPhase('step2');
      toast({ title: 'AI is unable to generate image', description: 'Please try again.', variant: 'destructive' });
    }
  };

  const handleRevealComplete = () => {
    setSelectedComboIndex(0);
    setPhase('combo-select');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleComboSelect = (idx: number) => {
    trackStep3Select((idx + 1) as 1 | 2 | 3);
    setSelectedComboIndex(idx);
  };

  const handleComboConfirm = async () => {
    const combo = combosList[selectedComboIndex];
    if (!combo) return;
    trackSubmitStep3();
    setPhase('generating');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const fallback = getPrimaryImage(combo.shirt) || '';
    let avatarUrl: string | null = null;

    if (selfieBase64) {
      const beltColor = combo.shoes?.combo_id?.includes('brown') ? 'Brown' : 'Black';
      try {
        const res = await fetch('/generate-final-look/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            selfie_image: selfieBase64,
            shirt_color: combo.shirt?.hex_code || '',
            pant_color: combo.pant?.hex_code || '',
            belt_shoes_combo_color: beltColor,
            height: `${feet}'${inches}`,
            weight,
            body_type: build,
            occasion,
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json() as { generated_image_url?: string };
        avatarUrl = data.generated_image_url?.trim() || fallback;
      } catch {
        avatarUrl = fallback;
      }
    } else {
      avatarUrl = fallback;
    }

    setResultCombo(combo);
    setResultAvatarUrl(avatarUrl);
    setResultAvatarError(!avatarUrl || avatarUrl === fallback);
    setPhase('result');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleComboSelectBack = () => {
    setPhase('step2');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResultBack = () => {
    setPhase('combo-select');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sequential background generation for combos 2 and 3 — fires after combo 1 is ready
  useEffect(() => {
    if (triggerBackgroundGen === 0 || combosList.length < 2 || !selfieBase64) return;

    let cancelled = false;

    const generateComboAvatar = async (combo: ComboEntry, idx: number) => {
      const beltColor = combo.shoes?.combo_id?.includes('brown') ? 'Brown' : 'Black';
      const fallback = getPrimaryImage(combo.shirt) || '';
      try {
        const res = await fetch('/generate-final-look/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            selfie_image: selfieBase64,
            shirt_color: combo.shirt?.hex_code || '',
            pant_color: combo.pant?.hex_code || '',
            belt_shoes_combo_color: beltColor,
            height: `${feet}'${inches}`,
            weight,
            body_type: build,
            occasion,
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json() as { generated_image_url?: string };
        if (!cancelled) {
          setAvatarUrls(prev => { const next = [...prev]; next[idx] = data.generated_image_url?.trim() || fallback; return next; });
        }
      } catch {
        if (!cancelled) {
          setAvatarUrls(prev => { const next = [...prev]; next[idx] = fallback; return next; });
          setAvatarErrors(prev => { const next = [...prev]; next[idx] = true; return next; });
        }
      }
    };

    // Sequential: generate combo 2, then combo 3
    (async () => {
      for (let i = 1; i < combosList.length; i++) {
        if (cancelled) break;
        await generateComboAvatar(combosList[i], i);
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerBackgroundGen]);

  if (phase === 'analyzing') {
    return (
      <>
        <Helmet><title>Analyzing Your Look — Manatomy AI</title></Helmet>
        <AnalyzingPhase selfiePreview={selfiePreview} />
      </>
    );
  }

  if (phase === 'generating') {
    return (
      <>
        <Helmet><title>Crafting Your Look — Manatomy AI</title></Helmet>
        <AnalyzingPhase
          selfiePreview={selfiePreview}
          title="Crafting your look..."
          subtitle="Placing the selected outfit on you"
        />
      </>
    );
  }

  if (phase === 'combo-select' && faceAnalysis && combosList.length > 0) {
    return (
      <>
        <Helmet><title>Choose Your Look — Manatomy AI</title></Helmet>
        <ComboSelectPhase
          combos={combosList}
          faceAnalysis={faceAnalysis}
          selectedIndex={selectedComboIndex}
          onSelect={handleComboSelect}
          onConfirm={handleComboConfirm}
          onBack={handleComboSelectBack}
        />
      </>
    );
  }

  if (phase === 'revealing' && faceAnalysis) {
    return (
      <>
        <Helmet><title>Your Style DNA — Manatomy AI</title></Helmet>
        <RevealingPhase
          skinTone={faceAnalysis.skin_tone}
          eyeColor={faceAnalysis.eye_color}
          hairColor={faceAnalysis.hair_color}
          selfiePreview={selfiePreview}
          onComplete={handleRevealComplete}
        />
      </>
    );
  }

  if (phase === 'result' && resultCombo) {
    return (
      <>
        <Helmet><title>Your Complete Look — Manatomy AI</title></Helmet>
        <ResultPhase
          combos={[resultCombo]}
          avatarUrls={[resultAvatarUrl]}
          avatarErrors={[resultAvatarError]}
          occasion={occasion}
          onBack={handleResultBack}
          mobile={mobile}
        />
      </>
    );
  }

  if (phase === 'step2') {
    return (
      <>
        <Helmet><title>Your Details — Manatomy AI</title></Helmet>
        <QuickHeader step={2} total={5} showBack onBack={() => setPhase('step1')} />
        <Step2Phase
          name={name} setName={setName}
          mobile={mobile} setMobile={setMobile}
          feet={feet} setFeet={setFeet}
          inches={inches} setInches={setInches}
          weight={weight} setWeight={setWeight}
          selfiePreview={selfiePreview}
          errors={errors}
          uploadRef={uploadRef as React.RefObject<HTMLInputElement>}
          cameraRef={cameraRef as React.RefObject<HTMLInputElement>}
          handleFileChange={handleFileChange}
          setErrors={setErrors}
          onBack={() => setPhase('step1')}
          onSubmit={handleStep2Submit}
        />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Get Your Complete Look Instantly — AI Outfit Picker | Manatomy AI</title>
        <meta name="description" content="Upload your selfie and let AI instantly pick your perfect shirt, pants, and shoes combo. One photo, one complete outfit — tailored to you." />
        <link rel="canonical" href="https://ai.themanatomy.com/quick-start" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <QuickHeader step={1} total={5} />
      <Step1Phase
        occasion={occasion} setOccasion={setOccasion}
        build={build} setBuild={setBuild}
        onContinue={handleStep1Continue}
      />
    </>
  );
};

export default QuickStart;
